/* Scenarios page: reveal-on-scroll, day/night hero, foundation carousel.
 *
 * Replaces the design's GSAP ScrollTrigger / React state with light DOM APIs.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

function initReveal() {
  const targets = [...document.querySelectorAll('[data-hf-reveal]')];
  if (targets.length === 0) return;

  if (REDUCED.matches || !('IntersectionObserver' in window)) {
    targets.forEach((t) => t.classList.add('is-revealed'));
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -15% 0px' }
  );

  targets.forEach((t) => io.observe(t));
}

function initHashLanding() {
  const { hash } = window.location;
  if (!hash || hash.length < 2) return;

  const target = document.getElementById(decodeURIComponent(hash.slice(1)));
  if (!target) return;

  target.classList.add('is-revealed');
  requestAnimationFrame(() =>
    target.scrollIntoView({ behavior: REDUCED.matches ? 'auto' : 'smooth', block: 'start' })
  );
}

function setHeroMode(hero, mode) {
  const day = hero.querySelector('.hf-scn-hero__img--day');
  const night = hero.querySelector('.hf-scn-hero__img--night');
  const buttons = hero.querySelectorAll('[data-hf-scn-mode]');
  const isDay = mode === 'day';

  day?.classList.toggle('is-active', isDay);
  night?.classList.toggle('is-active', !isDay);

  buttons.forEach((btn) => {
    const active = btn.dataset.hfScnMode === mode;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  });
}

function initHeroModes() {
  document.querySelectorAll('[data-hf-scn-hero]').forEach((hero) => {
    if (!hero.querySelector('.hf-scn-hero__img--night')) return;

    hero.querySelectorAll('[data-hf-scn-mode]').forEach((btn) => {
      btn.addEventListener('click', () => setHeroMode(hero, btn.dataset.hfScnMode));
    });
  });
}

function goToSlide(carousel, index) {
  const slides = [...carousel.querySelectorAll('[data-hf-scn-slide]')];
  const dots = [...carousel.querySelectorAll('[data-hf-scn-dot]')];
  if (slides.length === 0) return;

  const next = ((index % slides.length) + slides.length) % slides.length;

  slides.forEach((slide, i) => {
    const active = i === next;
    slide.classList.toggle('is-active', active);
    slide.setAttribute('aria-hidden', active ? 'false' : 'true');
  });

  dots.forEach((dot, i) => {
    const active = i === next;
    dot.classList.toggle('is-active', active);
    dot.setAttribute('aria-selected', active ? 'true' : 'false');
  });

  carousel.dataset.hfScnIndex = String(next);
}

function initCarousels() {
  document.querySelectorAll('[data-hf-scn-carousel]').forEach((carousel) => {
    const slides = carousel.querySelectorAll('[data-hf-scn-slide]');
    if (slides.length < 2) return;

    carousel.dataset.hfScnIndex = '0';

    carousel.querySelectorAll('[data-hf-scn-dot]').forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = Number(dot.dataset.hfScnDot || 0);
        goToSlide(carousel, idx);
        restartTimer(carousel);
      });
    });

    if (!REDUCED.matches) startTimer(carousel);
  });
}

function startTimer(carousel) {
  stopTimer(carousel);
  carousel._hfTimer = window.setInterval(() => {
    const current = Number(carousel.dataset.hfScnIndex || 0);
    goToSlide(carousel, current + 1);
  }, 4000);
}

function stopTimer(carousel) {
  if (carousel._hfTimer) {
    window.clearInterval(carousel._hfTimer);
    carousel._hfTimer = null;
  }
}

function restartTimer(carousel) {
  if (REDUCED.matches) return;
  startTimer(carousel);
}

function init() {
  initReveal();
  initHashLanding();
  initHeroModes();
  initCarousels();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

document.addEventListener('shopify:section:load', init);
